import React, { useState, useRef } from 'react';
import { useQuery, gql, useMutation } from '@apollo/client';
import {
  Table,
  Button,
  Typography,
  Input,
  Spin,
  Pagination,
  Modal,
  message,
  Popconfirm,
  Tooltip,
  Tag,
  Select,
  Space
} from 'antd';
import { WhatsAppOutlined, DeleteOutlined, SearchOutlined, CopyOutlined, FilterOutlined } from '@ant-design/icons';
import { useSearchBehavior } from '../hooks/useSearchBehavior';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import CampaignForm from '../components/campaigns/CampaignForm';
import CampaignDetailsModal from '../components/campaigns/CampaignDetailsModal';
import { DELETE_CAMPAIGN, DUPLICATE_CAMPAIGN } from '../mutations/campaign';
import { getKindTag } from '../utils/campaignUtils';
import { GET_TAGS } from '../mutations/tags';

const { Text } = Typography;

const GET_CAMPAIGNS = gql`
  query GetCampaigns($page: Int!, $perPage: Int!, $search: String!, $tagIds: [ID!], $kinds: [String!], $aggregatedStatuses: [String!]) {
    campaigns(page: $page, perPage: $perPage, search: $search, tagIds: $tagIds, kinds: $kinds, aggregatedStatuses: $aggregatedStatuses) {
      totalCount
      edges {
        node {
          id
          name
          kind
          kindLabel
          campaignMessagesCount
          createdAt
          campaignMode
          isPendingOnly
          discountPercentage
          expectedCustomersCount
          onlyVerifiedWhatsappCampaigns
          aggregatedStatus
          group {
            id
            name
          }
          selectedProducts {
            id
            name
          }
          tags {
            id
            name
            backgroundColor
            fontColor
          }
        }
      }
    }
    campaignsCount(search: $search, tagIds: $tagIds, kinds: $kinds, aggregatedStatuses: $aggregatedStatuses)
  }
`;

const kindOptions = [
  { value: 'offer', label: 'Oferta' },
  { value: 'reactivation', label: 'Reativação' },
  { value: 'activation', label: 'Ativação' },
  { value: 'follow_up', label: 'Follow Up' }
];

const aggregatedStatusOptions = [
  { value: 'read', label: 'Lidas' },
  { value: 'delivered', label: 'Entregues' },
  { value: 'sent', label: 'Enviadas' },
  { value: 'partially_sent', label: 'Parcialmente enviadas' },
  { value: 'pending', label: 'Pendente' },
  { value: 'scheduled', label: 'Agendada' },
  { value: 'failed', label: 'Falhas' }
];

const aggregatedStatusMeta = {
  read: { label: 'Lidas', color: 'blue' },
  delivered: { label: 'Entregues', color: 'cyan' },
  sent: { label: 'Enviadas', color: 'success' },
  partially_sent: { label: 'Parcialmente enviadas', color: 'processing' },
  pending: { label: 'Pendente', color: 'warning' },
  scheduled: { label: 'Agendada', color: 'purple' },
  failed: { label: 'Falhas', color: 'error' }
};

const Campaigns = () => {
  const [isFormModalVisible, setIsFormModalVisible] = useState(false);
  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false);
  const [isDuplicateModalVisible, setIsDuplicateModalVisible] = useState(false);
  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [initialCampaignState, setInitialCampaignState] = useState(null);
  const [campaignToDuplicate, setCampaignToDuplicate] = useState(null);
  const [duplicateCampaignName, setDuplicateCampaignName] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [selectedKinds, setSelectedKinds] = useState([]);
  const [selectedAggregatedStatuses, setSelectedAggregatedStatuses] = useState([]);
  
  // Usa o hook customizado para gerenciar o comportamento de busca
  const {
    searchTerm: search,
    setSearchTerm: setSearch,
    activeSearchTerm,
    handleManualSearch,
    clearSearch,
    isInstantSearch
  } = useSearchBehavior('');

  const searchInputRef = useRef(null);
  const activeFiltersCount = selectedTagIds.length + selectedKinds.length + selectedAggregatedStatuses.length;

  const { data: tagsData } = useQuery(GET_TAGS, {
    variables: {
      page: 1,
      perPage: 200,
      search: '',
      activeOnly: false,
      advancedFilters: []
    },
    fetchPolicy: 'network-only'
  });

  const [deleteCampaign] = useMutation(DELETE_CAMPAIGN, {
    onCompleted: (data) => {
      if (data.deleteCampaign.success) {
        message.success('Campanha excluída com sucesso!');
        refetch();
      } else {
        message.error(data.deleteCampaign.errors[0] || 'Erro ao excluir campanha');
      }
    },
    onError: (error) => {
      message.error('Erro ao excluir campanha: ' + error.message);
    }
  });

  const [duplicateCampaign] = useMutation(DUPLICATE_CAMPAIGN, {
    onCompleted: (data) => {
      const result = data?.duplicateCampaign;

      if (result?.errors?.length > 0 || !result?.campaign) {
        message.error(result?.errors?.[0] || 'Erro ao duplicar campanha');
        return;
      }

      setInitialCampaignState({
        campaignId: result.campaign.id,
        name: result.campaign.name,
        kind: result.campaign.kind,
        campaignMode: result.campaign.campaignMode,
        expectedCustomersCount: result.campaign.expectedCustomersCount,
        totalMessagesCount: result.campaignMessagesCount || 0
      });
      setIsFormModalVisible(true);
      message.success('Campanha duplicada com sucesso! Defina o novo agendamento.');
      refetch();
    },
    onError: (error) => {
      message.error('Erro ao duplicar campanha: ' + error.message);
    }
  });

  // Remove o useEffect do debounce, agora é gerenciado pelo hook customizado

  const { loading, error, data, refetch } = useQuery(GET_CAMPAIGNS, {
    variables: {
      page,
      perPage,
      search: activeSearchTerm,
      tagIds: selectedTagIds.length > 0 ? selectedTagIds : null,
      kinds: selectedKinds.length > 0 ? selectedKinds : null,
      aggregatedStatuses: selectedAggregatedStatuses.length > 0 ? selectedAggregatedStatuses : null
    },
    fetchPolicy: 'no-cache',
  });

  const handleShowCreateModal = () => {
    setInitialCampaignState(null);
    setIsFormModalVisible(true);
  };

  const handleFormCancel = () => {
    setIsFormModalVisible(false);
    setInitialCampaignState(null);
  };

  const handleDuplicateCancel = () => {
    setIsDuplicateModalVisible(false);
    setCampaignToDuplicate(null);
    setDuplicateCampaignName('');
  };

  const handleViewCampaign = (campaign) => {
    setSelectedCampaign(campaign);
    setIsDetailsModalVisible(true);
  };

  const handleCloseDetailsModal = () => {
    setIsDetailsModalVisible(false);
    setSelectedCampaign(null);
    refetch(); // Refresh the list after viewing details (in case changes were made)
  };

  const handlePageChange = (newPage, newPageSize) => {
    setPage(newPage);
    setPerPage(newPageSize);
  };

  const handleOpenFilterModal = () => {
    setIsFilterModalVisible(true);
  };

  const handleCloseFilterModal = () => {
    setIsFilterModalVisible(false);
  };

  const handleApplyFilters = () => {
    setPage(1);
    setIsFilterModalVisible(false);
  };

  const handleClearAllFilters = () => {
    setSelectedTagIds([]);
    setSelectedKinds([]);
    setSelectedAggregatedStatuses([]);
    setPage(1);
  };

  const handleRemoveTagFilter = (tagId) => {
    setSelectedTagIds((current) => current.filter((id) => id !== tagId));
    setPage(1);
  };

  const handleRemoveKindFilter = (kind) => {
    setSelectedKinds((current) => current.filter((item) => item !== kind));
    setPage(1);
  };

  const handleRemoveAggregatedStatusFilter = (status) => {
    setSelectedAggregatedStatuses((current) => current.filter((item) => item !== status));
    setPage(1);
  };

  const handleSearch = () => {
    if (!isInstantSearch) {
      handleManualSearch();
    }
    setPage(1);
  };

  const handleFormFinish = (result) => {
    setIsFormModalVisible(false);
    setInitialCampaignState(null);
    message.success(`Campanha criada com sucesso! ${result.messagesCount} mensagens foram criadas.`);
    refetch();
  };

  const handleDelete = (campaignId) => {
    deleteCampaign({
      variables: { id: campaignId }
    });
  };

  const handleDuplicate = (campaign) => {
    setCampaignToDuplicate(campaign);
    setDuplicateCampaignName(`${campaign.name} - Cópia`);
    setIsDuplicateModalVisible(true);
  };

  const handleConfirmDuplicate = () => {
    if (!campaignToDuplicate) {
      return;
    }

    const trimmedName = duplicateCampaignName.trim();

    if (!trimmedName) {
      message.error('Informe o nome da campanha duplicada');
      return;
    }

    duplicateCampaign({
      variables: { id: campaignToDuplicate.id, name: trimmedName }
    });

    setIsDuplicateModalVisible(false);
    setCampaignToDuplicate(null);
    setDuplicateCampaignName('');
  };

  const columns = [
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Tipo',
      dataIndex: 'kind',
      key: 'kind',
      render: (_, record) => getKindTag(record.kind),
    },
    {
      title: 'Tags',
      key: 'tags',
      render: (_, record) => (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
          {record.tags && record.tags.length > 0 ? (
            record.tags.map(tag => (
              <Tag 
                key={tag.id}
                color={tag.backgroundColor || '#6fa8dc'} 
                style={{ 
                  color: tag.fontColor || '#000000',
                  border: 'none',
                  fontSize: '12px',
                  padding: '2px 6px'
                }}
              >
                {tag.name}
              </Tag>
            ))
          ) : (
            <span style={{ color: '#999', fontSize: '12px' }}>Nenhuma tag</span>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'aggregatedStatus',
      key: 'aggregatedStatus',
      render: (aggregatedStatus) => {
        if (!aggregatedStatus) {
          return '-';
        }

        const statusMeta = aggregatedStatusMeta[aggregatedStatus] || { label: aggregatedStatus, color: 'default' };

        return (
          <Tag color={statusMeta.color}>
            {statusMeta.label}
          </Tag>
        );
      },
    },
    {
      title: 'Desconto',
      dataIndex: 'discountPercentage',
      key: 'discountPercentage',
      align: 'center',
      render: (discount) => discount ? `${discount}%` : '-',
    },
    {
      title: 'Grupo',
      key: 'group',
      render: (_, record) => record.group?.name || '-',
    },
    {
      title: 'WhatsApp Validado',
      dataIndex: 'onlyVerifiedWhatsappCampaigns',
      key: 'onlyVerifiedWhatsappCampaigns',
      width: 150,
      align: 'center',
      render: (onlyVerified) => (
        <Tag color={onlyVerified ? 'green' : 'default'}>
          {onlyVerified ? 'Apenas Validados' : 'Todos'}
        </Tag>
      ),
    },
    {
      title: 'Mensagens',
      dataIndex: 'campaignMessagesCount',
      key: 'campaignMessagesCount',
      align: 'center',
    },
    {
      title: 'Data de Criação',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => format(new Date(date), 'dd/MM/yyyy HH:mm', { locale: ptBR }),
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (_, record) => (
        <>
          <Button 
            type="link"
            onClick={() => handleViewCampaign(record)}
          >
            Visualizar
          </Button>
          <Button
            type="link"
            icon={<CopyOutlined />}
            onClick={() => handleDuplicate(record)}
          >
            Duplicar
          </Button>
          <Tooltip title={record.isPendingOnly ? 'Excluir campanha' : 'Não é possível excluir campanhas com mensagens agendadas ou enviadas'}>
            <Popconfirm
              title="Tem certeza que deseja excluir esta campanha?"
              onConfirm={() => handleDelete(record.id)}
              okText="Sim"
              cancelText="Não"
              disabled={!record.isPendingOnly}
            >
              <Button 
                type="link" 
                danger 
                icon={<DeleteOutlined />} 
                disabled={!record.isPendingOnly}
              />
            </Popconfirm>
          </Tooltip>
        </>
      ),
    },
  ];

  if (error) return <Text type="danger">Error loading campaigns: {error.message}</Text>;

  const campaignData = data?.campaigns?.edges.map(({ node }) => ({
    key: node.id,
    id: node.id,
    name: node.name,
    kind: node.kind,
    kindLabel: node.kindLabel,
    campaignMessagesCount: node.campaignMessagesCount,
    createdAt: node.createdAt,
    isPendingOnly: node.isPendingOnly,
    aggregatedStatus: node.aggregatedStatus,
    discountPercentage: node.discountPercentage,
    expectedCustomersCount: node.expectedCustomersCount,
    onlyVerifiedWhatsappCampaigns: node.onlyVerifiedWhatsappCampaigns,
    group: node.group,
    tags: node.tags,
  })) || [];

  const tagOptions = tagsData?.tags?.edges?.map(({ node }) => ({
    value: node.id,
    label: node.name
  })) || [];

  const selectedTagsMap = new Map(tagOptions.map((tag) => [String(tag.value), tag.label]));

  return (
    <>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center' }}>
        <Input
          ref={searchInputRef}
          placeholder={isInstantSearch ? "Pesquisar campanhas (busca automática)" : "Pesquisar campanhas"}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ marginRight: 8, width: 300 }}
          onPressEnter={handleSearch}
          autoFocus
          allowClear={true}
        />
        {!isInstantSearch && (
          <Button 
            type="primary" 
            icon={<SearchOutlined />}
            onClick={handleSearch}
          >
            Pesquisar
          </Button>
        )}
        <Button
          type={activeFiltersCount > 0 ? 'primary' : 'default'}
          icon={<FilterOutlined />}
          onClick={handleOpenFilterModal}
          style={{ marginLeft: 8 }}
        >
          Filtros
          {activeFiltersCount > 0 && (
            <span style={{ marginLeft: 4, backgroundColor: '#ff4d4f', color: 'white', borderRadius: '10px', padding: '2px 6px', fontSize: '10px' }}>
              {activeFiltersCount}
            </span>
          )}
        </Button>
        <Button 
          type="primary" 
          onClick={handleShowCreateModal}
          icon={<WhatsAppOutlined />}
          style={{ marginLeft: 'auto' }}
        >
          Nova Campanha
        </Button>
      </div>

      {activeFiltersCount > 0 && (
        <div style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
          {selectedTagIds.map((tagId) => (
            <Tag key={`tag-${tagId}`} closable onClose={() => handleRemoveTagFilter(tagId)} color="blue">
              TAG: {selectedTagsMap.get(String(tagId)) || tagId}
            </Tag>
          ))}
          {selectedKinds.map((kind) => (
            <Tag key={`kind-${kind}`} closable onClose={() => handleRemoveKindFilter(kind)} color="geekblue">
              Tipo: {kindOptions.find((option) => option.value === kind)?.label || kind}
            </Tag>
          ))}
          {selectedAggregatedStatuses.map((status) => (
            <Tag key={`status-${status}`} closable onClose={() => handleRemoveAggregatedStatusFilter(status)} color="purple">
              Status: {aggregatedStatusMeta[status]?.label || status}
            </Tag>
          ))}
          <Button type="link" onClick={handleClearAllFilters}>
            Limpar filtros
          </Button>
        </div>
      )}

      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={campaignData}
          pagination={false}
          rowKey="id"
          scroll={{ x: 'max-content' }}
        />
      </Spin>

      <Pagination
        current={page}
        pageSize={perPage}
        total={data?.campaignsCount || 0}
        locale={{ items_per_page: '/ página' }}
        onChange={handlePageChange}
      />

      <Modal
        title="Filtros de campanhas"
        open={isFilterModalVisible}
        onOk={handleApplyFilters}
        onCancel={handleCloseFilterModal}
        okText="Aplicar"
        cancelText="Cancelar"
        destroyOnClose
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Text strong>TAG</Text>
            <Select
              mode="multiple"
              placeholder="Selecione as tags"
              value={selectedTagIds}
              onChange={setSelectedTagIds}
              options={tagOptions}
              style={{ width: '100%', marginTop: 8 }}
              optionFilterProp="label"
              allowClear
            />
          </div>

          <div>
            <Text strong>Tipo</Text>
            <Select
              mode="multiple"
              placeholder="Selecione os tipos"
              value={selectedKinds}
              onChange={setSelectedKinds}
              options={kindOptions}
              style={{ width: '100%', marginTop: 8 }}
              optionFilterProp="label"
              allowClear
            />
          </div>

          <div>
            <Text strong>Status</Text>
            <Select
              mode="multiple"
              placeholder="Selecione os status"
              value={selectedAggregatedStatuses}
              onChange={setSelectedAggregatedStatuses}
              options={aggregatedStatusOptions}
              style={{ width: '100%', marginTop: 8 }}
              optionFilterProp="label"
              allowClear
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={handleClearAllFilters}>
              Limpar tudo
            </Button>
          </div>
        </Space>
      </Modal>

      <Modal
        title="Duplicar Campanha"
        open={isDuplicateModalVisible}
        onOk={handleConfirmDuplicate}
        onCancel={handleDuplicateCancel}
        okText="OK"
        cancelText="Cancelar"
        confirmLoading={false}
        destroyOnClose
      >
        <Input
          placeholder="Nome da campanha"
          value={duplicateCampaignName}
          onChange={(e) => setDuplicateCampaignName(e.target.value)}
          onPressEnter={handleConfirmDuplicate}
          autoFocus
        />
      </Modal>

      <Modal
        title={initialCampaignState ? 'Agendar Campanha Duplicada' : 'Criar Campanha'}
        open={isFormModalVisible}
        onCancel={handleFormCancel}
        footer={null}
        maskClosable={false}
        width={1200}
        getContainer={false}
        destroyOnClose
      >
        <CampaignForm
          initialCampaignState={initialCampaignState}
          onFinish={handleFormFinish}
          onCancel={handleFormCancel}
        />
      </Modal>

      <Modal
        title="Detalhes da Campanha"
        open={isDetailsModalVisible}
        onCancel={handleCloseDetailsModal}
        footer={null}
        width={1200}
        centered
        destroyOnClose
      >
        {selectedCampaign && (
          <CampaignDetailsModal
            campaignId={selectedCampaign.id}
            onClose={handleCloseDetailsModal}
          />
        )}
      </Modal>
    </>
  );
};

export default Campaigns;